import html2canvas from "html2canvas";
import moment from "moment";

import {
  CHARGE_BALANCE_THRESHOLD,
  CHEMICALS_SYMBOL_MAPPING,
  DATE_FORMAT,
  TIME_UNITS,
} from "../constants/global.constants";

const featureIdToField = {
  1: "mCIP",
  2: "CEB",
};

export const getDisabledUFTech = (ufSpecialFeatureID) =>
  featureIdToField[ufSpecialFeatureID] || "";

export const convertUpto2Digits = (value) =>
  isNaN(parseFloat(value)) ? "0" : parseFloat(value).toFixed(2);

export const convertUptoDigits = (value, digits = 2) => {
  const tempValue = isNaN(value) ? 0 : parseFloat(value);
  return (tempValue && tempValue.toFixed(digits)) || 0;
};

/**
 * Changes the format of a chemical symbol based on a predefined mapping.
 * Replace each occurrence of the 'from' symbol with the 'to' symbol
 *
 * @param {string} chemicalSymbol - The chemical symbol to be formatted.
 * @return {string} - The formatted chemical symbol.
 */
export const changeChemicalFormat = (chemicalSymbol) => {
  if (!chemicalSymbol) return "0";

  for (const [from, to] of Object.entries(CHEMICALS_SYMBOL_MAPPING)) {
    chemicalSymbol = `${chemicalSymbol}`.replaceAll(from, to);
  }

  return chemicalSymbol;
};

/**
 * Converts a source object into a new object with keys appended by a type.
 * @param {Object} source - The source object.
 * @param {string} type - The type to append to each key.
 * @return {Object} - The new object with keys appended by the type.
 */
export const getChemicalObject = (source, type, disabledField) =>
  Object.entries(source).reduce((result, [key, value]) => {
    const paramValue =
      disabledField && key.includes(disabledField) ? "0" : value;
    result[`${key}_${type}`] = paramValue;
    return result;
  }, {});

export const getFolderName = (foldername) =>
  foldername?.length > 20 ? `${foldername?.substring(0, 20)}...` : foldername;

export const debounce = (callback, timeout = 500) => {
  let timer;
  return (...args) => {
    clearTimeout(timer);
    timer = setTimeout(() => {
      callback(...args);
    }, timeout);
  };
};

const validData = (data, label, flag) => {
  const den = flag ? "0" : 0;
  return data ? data[label] : den;
};

export const formatChemicalSymbol = (chemicalSymbol, flag, field = "symbol") =>
  changeChemicalFormat(validData(chemicalSymbol, field, flag)).toString();

export const calculateSum = (...args) => {
  let arr = args;
  // If the first argument is an array, use it; otherwise, use all arguments as array
  if (args.length === 1 && Array.isArray(args[0])) {
    arr = args[0];
  }
  return arr.reduce((acc, val) => {
    const value = parseFloat(val) || 0;
    return acc + value;
  }, 0);
};

/**
 * Calculates the difference between two dates.
 *
 * @param {string|Date|moment.Moment} date1 - The first date.
 * @param {string|Date|moment.Moment} date2 - The second date.
 * @returns {number} The difference between the two dates in milliseconds.
 */
export const getDateDiff = (date1, date2) => moment(date1).diff(moment(date2));

/**
 * Creates a comparator function for sorting objects based on a specified key, type, and order.
 *
 * @param {Object} options - The options for the comparator.
 * @param {string} options.key - The key of the object to sort by.
 * @param {string} options.type - The type of the value to sort ("string" or "date").
 * @param {string} [options.order="asc"] - The order of sorting ("asc" for ascending or "desc" for descending).
 * @param {boolean} [options.useNumeric] - Whether to use numeric collation for string comparison.
 * @returns {Function} A comparator function that can be used in array sort method.
 */
export const sortComparator =
  ({ key, type, order = "asc", useNumeric, aliasKeys }) =>
  (a, b) => {
    const getValue = (obj) => {
      if (obj[key] === undefined && isIterable(aliasKeys)) {
        for (const aliaskey of aliasKeys) {
          if (obj[aliaskey] !== undefined) {
            return obj[aliaskey];
          }
        }
      }
      return obj[key];
    };

    let valA = getValue(a, key, aliasKeys);
    let valB = getValue(b, key, aliasKeys);

    if (order === "desc") {
      // swapping the variables for desc order
      [valA, valB] = [valB, valA];
    }
    if (type === "string") {
      return valA?.localeCompare(valB, undefined, { numeric: useNumeric });
    } else if (type === "date") {
      // date should be in ISO Format
      return getDateDiff(valA, valB);
    }
    return valA - valB;
  };

/**
 * Multi-sort utility function using sortComparator
 * @param {Array} array - The array to sort
 * @param {Array} criteria - An array of sorting criteria follows sortComparator params
 * @returns {Array} - The sorted array
 */
export const multiSort = (array, criteria) =>
  array.sort((a, b) => {
    for (const criterion of criteria) {
      const result = sortComparator(criterion)(a, b);
      if (result !== 0) {
        return result; // If comparison is not equal, return the result
      }
    }
    return 0; // If all criteria are equal, return 0
  });

// function returns true if all doses are disabled or total dose value is 0
export const isDoseErr = ({ indicators, doseValues }) => {
  const isAllDosesDisabled = indicators.every((indicator) => !indicator);
  const totalDoseValue = calculateSum(doseValues);
  return isAllDosesDisabled || totalDoseValue === 0;
};

export const loadExternalScript = (url) => {
  if (url) {
    const script = document.createElement("script");
    script.src = url;
    document.body.appendChild(script);
  }
};

export const isValueInRange = (value, ranges) => {
  if (isNaN(value) || !ranges) return false;
  const { minValue, maxValue } = ranges;

  const val = parseFloat(value);
  return val >= minValue && val <= maxValue;
};

export const groupBy = (array, key) => {
  if (!array || !key) return {};
  return array.reduce((acc, currentValue) => {
    const groupKey = currentValue[key];
    if (!acc[groupKey]) {
      acc[groupKey] = [];
    }
    acc[groupKey].push(currentValue);
    return acc;
  }, {});
};

/**
 * Converts an array of objects into an object, using a specified key from each object as the key for the new object.
 *
 * @param {Array} array - The array of objects to convert.
 * @param {string} key - The key to use for the new object.
 * @returns {Object} - The resulting object with keys derived from the specified key in each object.
 */
export const convertListToObj = (array, key) => {
  if (!array || !key) return {};
  return array.reduce((acc, item) => ({ ...acc, [item[key]]: item }), {});
};

/**
 * Clones an array and overrides specific objects within it based on provided overrides.
 *
 * @param {Array<Object>} array - The original array to be cloned and modified.
 * @param {Object} overrides - An object containing keys that match the `arrayObjkey` values in the array and values that are objects with properties to override.
 * @param {string} arrayObjkey - The key in the array objects that will be used to match against the keys in the overrides object.
 * @returns {Array<Object>} - A new array with the specified overrides applied.
 */
export const cloneArrayAndOverride = (array, overrides, arrayObjkey) => {
  const clonedArray = structuredClone(array);
  Object.entries(overrides).forEach(([overrideValueKey, OverrideValueData]) => {
    const arrayOverrideIndex = clonedArray.findIndex(
      (obj) => obj[arrayObjkey] === overrideValueKey,
    );
    if (arrayOverrideIndex !== -1) {
      clonedArray[arrayOverrideIndex] = {
        ...clonedArray[arrayOverrideIndex],
        ...OverrideValueData,
      };
    }
  });
  return clonedArray;
};

/**
 * Capitalizes the first character of the input string.
 *
 * @param {string} value - The input string to be capitalized.
 * @return {string} - The input string with the first character capitalized.
 */
export const capitalize = (value) => {
  if (!value) return "";
  return value.charAt(0).toUpperCase() + value.slice(1);
};

/**
 * Formats a date using the moment library.
 *
 * @param {string|Date} date - The date to be formatted.
 * @param {string} format - The format string to use.
 * @return {string} - The formatted date string.
 */
export const formatDate = (date, format = DATE_FORMAT) =>
  date ? moment(date).format(format) : "-";

/**
 * Converts the HTML content of a specified selector to a Base64-encoded PNG image.
 *
 * @param {string} selector - The CSS selector of the HTML element to convert.
 * @param {Object} options - The options to pass to the html2canvas function.
 * @returns {Promise<string>} A promise that resolves to a Base64-encoded PNG image.
 * @throws Will log an error message if the conversion fails.
 */
export const convertHtmlToBase64 = async (selector, options) => {
  try {
    const generatedCanvas = await html2canvas(
      document.querySelector(selector),
      options,
    );
    return generatedCanvas.toDataURL("image/png");
  } catch (error) {
    // DupontLogger("error", "Error in converting system diagram to base64", error);
    console.log("error", "Error in converting system diagram to base64", error);
  }
};

/**
 * Combines multiple class names into a single string.
 *
 * @param {...string} classes - The class names to combine.
 * @returns {string} A single string with all the class names combined, separated by spaces.
 */
export const combineClassNames = (...classes) =>
  classes.filter(Boolean).join(" ").trim();

/**
 * Checks if a value is null or undefined.
 *
 * @param {*} value - The value to check.
 * @returns {boolean} True if the value is null or undefined, false otherwise.
 */
export const isNullOrUndefined = (value) =>
  value === null || value === undefined;

export const isProdEnv = () =>
  process.env.REACT_APP_PROJECT_ENV?.toUpperCase() === "PROD";

export const isDevEnv = () =>
  process.env.REACT_APP_PROJECT_ENV?.toUpperCase() === "DEV";

/**
 * Swaps the positions of two elements in an array and updates their displayOrder properties.
 *
 * @param {Array} cases - The array of case objects.
 * @param {number} index1 - The index of the first element to swap.
 * @param {number} index2 - The index of the second element to swap.
 * @returns {Array} - A new array with the elements at index1 and index2 swapped and their displayOrder properties updated.
 */
export const swapData = (cases, index1, index2) => {
  const newCases = cases.map((caseItem) => ({ ...caseItem }));
  [newCases[index1], newCases[index2]] = [newCases[index2], newCases[index1]];

  // Update displayOrder
  const tempDisplayOrder = newCases[index1].displayOrder;
  newCases[index1].displayOrder = newCases[index2].displayOrder;
  newCases[index2].displayOrder = tempDisplayOrder;

  return newCases;
};

/**
 * Memoizes a given function by caching its results.
 *
 * @param {Function} fn - The function to be memoized.
 * @returns {Function} - A new function that caches the results of the original function.
 *
 * The returned function takes any number of arguments and checks if the result for those arguments
 * is already in the cache. If it is, it returns the cached result. If not, it calls the original
 * function with the arguments, stores the result in the cache, and then returns the result.
 */
export const memoizedFun = (fn) => {
  const cache = {};
  return (...args) => {
    const key = args.toString();
    if (cache[key]) {
      return cache[key];
    }
    const result = fn(...args);
    cache[key] = result;
    return result;
  };
};

export const isEmptyObject = (obj) => !obj || Object.keys(obj).length === 0;

export const isSelectedTreatment = (
  treatmentName,
  systemDesignCaseTreatmentVM,
) =>
  systemDesignCaseTreatmentVM?.find((item) =>
    item.treatmentName.includes(treatmentName),
  );

export const truncateText = (text, length) => {
  if (text.length > length) {
    return `${text.substring(0, length)}...`;
  }
  return text;
};

/**
 * Truncates a floating-point number to a specified number of decimal places.
 *
 * @param {number} number - The number to be truncated.
 * @param {number} [decimalPlaces=2] - The number of decimal places to keep. Defaults to 2.
 * @returns {number} - The truncated number.
 *
 * e.g it will truncate 5.239999999 => 5.23, when decimalPlaces is 2 and 5.239, when it is 3
 */
export const truncateFloat = (number, decimalPlaces = 2) => {
  const factor = Math.pow(10, decimalPlaces);
  return Math.floor(number * factor) / factor;
};

export const getFileExtension = (fileName) => fileName.split(".").pop();

export const isValuesZero = (...values) =>
  values.every((value) => parseFloat(value) === 0);

export const isChargeBalApproxZero = (chargeBal) => {
  if (chargeBal === undefined) return false;

  const num = Math.abs(Number(chargeBal));

  return num <= CHARGE_BALANCE_THRESHOLD;
};

/**
 * Converts a snake_case string to camelCase.
 *
 * @param {string} str - The snake_case string to convert.
 * @return {string} - The converted camelCase string.
 */
export const snakeToCamelCase = (str = "") =>
  str.replace(/(_\w)/g, (matches) => matches[1].toUpperCase());

export const injectValues = (str, value) => {
  Object.entries(value).forEach(([key, val]) => {
    const replaceStr = "${" + key + "}";
    str = str.replace(replaceStr, val);
  });
  return str;
};

export const getTrimedTextInLowerCase = (text) => text?.toLowerCase().trim();

export const getSupportedTechnology = (technologyName) => {
  if (technologyName === "" || technologyName === undefined) return null;
  return technologyName.split(",");
};

export const isArrayEqual = (a, b) => {
  if (a === null || a == undefined || b === null || b === undefined)
    return false;
  if (a?.length !== b?.length) return false;
  const tempArr1 = Array.isArray(a) ? [...a] : [];
  const tempArr2 = Array.isArray(b) ? [...b] : [];
  tempArr1.sort();
  tempArr2.sort();
  for (let i = 0; i < tempArr1.length; i++) {
    if (tempArr1[i] !== tempArr2[i]) return false;
  }
  return true;
};

/**
 * creating a helper function for deep comparision of two arrays or array of objects instead of using json.Stringify
 * @param {Object} object1 - The first object to compare.
 * @param {Object} object2 - The second object to compare.
 *  */
export const isArrayDataSame = (object1, object2) => {
  if (object1 === object2) return true;
  if (
    !object1 ||
    !object2 ||
    typeof object1 !== "object" ||
    typeof object2 !== "object"
  )
    return false;

  const keys1 = Object.keys(object1);
  const keys2 = Object.keys(object2);

  if (keys1.length !== keys2.length) return false;
  for (const key of keys1) {
    if (!keys2.includes(key) || !isArrayDataSame(object1[key], object2[key])) {
      return false;
    }
  }
  return true;
};

const getTimeDiffs = (time, formatter) => {
  const now = moment();
  time = moment(formatter(time, false));
  return Object.entries(TIME_UNITS).reduce(
    (acc, [unit]) => ({ ...acc, [unit]: now.diff(time, unit) }),
    {},
  );
};

/**
 * Returns a human-readable time difference based on the current time.
 *
 * @param {string|Date|moment.Moment} time - The time to compare with the current time.
 * @returns {string} - A human-readable time difference.
 */
export const getTimeDifference = (t, time, formatter, displayYears) => {
  const diff = getTimeDiffs(time, formatter);

  const getTimeString = (unit, type) => {
    const infoKey = unit > 1 ? `${type}sAgo` : `${type}Ago`;
    return t(infoKey).replace(`${time}`, `${unit}`);
  };

  if (diff.minutes < 1) {
    return t("justNow");
  } else if (diff.minutes < 60) {
    return getTimeString(diff.minutes, TIME_UNITS.minutes);
  } else if (diff.hours < 24) {
    return getTimeString(diff.hours, TIME_UNITS.hours);
  } else if (diff.days < 2 && !displayYears) {
    return getTimeString(1, TIME_UNITS.days);
  } else if (displayYears) {
    if (diff.years > 0) {
      return getTimeString(diff.years, TIME_UNITS.years);
    } else if (diff.months > 0) {
      return getTimeString(diff.months, TIME_UNITS.months);
    } else if (diff.weeks > 0) {
      return getTimeString(diff.weeks, TIME_UNITS.weeks);
    } else {
      return getTimeString(diff.days, TIME_UNITS.days);
    }
  } else {
    return formatter ? formatter(time) : moment(time).format(DATE_FORMAT);
  }
};

export const isIterable = (obj) =>
  obj != null && typeof obj[Symbol.iterator] === "function";

/**
 * Converts a value to a number. Defaults to 0 if the value is invalid.
 *
 * @param {*} value - The value to convert.
 * @returns {number} - The converted number or 0.
 */
export const convertToNumber = (value) => {
  const num = Number(value ?? 0);
  return isNaN(num) ? 0 : num;
};

// export const getFiles = () => {
//   const currentLanguage = i18n.language || USER_INFO.cultureName;
//   const context = require.context("../locales", true, /\.json$/);
//   const files = context.keys().filter(fileName => fileName.includes(`/${currentLanguage}/`));
//   return files.map(fileName => fileName.replace(`./${currentLanguage}/`, ""));
// };

export const scrollToTop = () => {
  window.scrollTo({
    top: 0,
    left: 0,
    behavior: "smooth",
  });
};

// Sums the values of two objects for matching keys: if both values are numbers, their sum is used; otherwise, the value from the first object is used.
export const sumTwoObjects = (obj1, obj2) =>
  Object.keys(obj1).reduce((result, key) => {
    const val1 = Number(obj1[key]);
    const val2 = Number(obj2[key]);
    result[key] = !isNaN(val1) && !isNaN(val2) ? val1 + val2 : obj1[key];
    return result;
  }, {});

// Returns a new object with only the specified keys from the input object.
export const getSubsetOfObject = (obj, keys) => {
  if (!obj || !keys || !Array.isArray(keys)) return {};
  return keys.reduce((result, key) => {
    if (key in obj) {
      result[key] = obj[key];
    }
    return result;
  }, {});
};

export const extractNumber = (sourceString) => {
  const match = sourceString.match(/\d+$/); // Matches digits at the end of the string
  return match ? parseInt(match[0], 10) : null;
};

export const replaceArrayValuesAtIndex = (arr, indexObj) =>
  arr.map((item, ind) => indexObj[ind] || item);

export const getErrorMessage = (error, errorKey) => {
  const code = error?.response?.data?.responseCode;
  if (code) {
    return `errorCode.${code}`;
  }
  return errorKey || "requestFailed";
};

export const replacePlaceholders = (str, replacements) =>
  Object.entries(replacements).reduce(
    (acc, [key, value]) =>
      acc.replace(new RegExp(`\\$\\{${key}\\}`, "g"), value),
    str,
  );

export const joinWithAnd = (...args) => {
  const arr = Array.isArray(args[0]) ? args[0] : args;
  const filtered = arr.filter(Boolean);
  const len = filtered.length;
  if (!len) return "";
  if (len === 1) return filtered[0];
  return (
    filtered.slice(0, -1).join(", ") +
    (len > 1 ? " & " + filtered[len - 1] : "")
  );
};

export const getSupportedFileTypes = (arr, prefix = "", islower = null) => {
  if (!Array.isArray(arr)) return "";

  const filesTypes = arr.map((item) => {
    const type = `${prefix}${
      islower === null
        ? item
        : islower
          ? item.toLowerCase()
          : item.toUpperCase()
    }`;
    return type;
  });
  return filesTypes.join(", ");
};
